import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import { useAppSearchBarQuery } from 'ecosystem-marketplace-core/apps/hooks/useAppSearchBarQuery';
import { useThemeSearchBarQuery } from 'ecosystem-marketplace-core/assets/hooks/useThemeSearchBarQuery';
import { useProfileSearchBarQuery } from 'ecosystem-marketplace-core/solutions/hooks/useProfileSearchBarQuery';
import CoreSearchBar from 'ecosystem-marketplace-core/components/storefront/SearchBar';
import * as RequestStates from 'ecosystem-marketplace-core/constants/RequestState';
import { TYPEAHEAD_SEARCH_MIN_CHARS } from 'ecosystem-marketplace-core/constants/search';
import { useMounted } from 'ecosystem-marketplace-core/hooks/useMounted';
import { debounce } from 'ecosystem-marketplace-core/utils/debounce';
import { isPending } from 'ecosystem-marketplace-core/utils/requestState';
import { getAppMarketplaceUrl, getAssetMarketplaceUrl, getSolutionsDirectoryUrl } from '../utils/urls';
const SearchCategory = {
  ALL: 'ALL',
  APPS: 'APPS',
  THEMES: 'THEMES',
  SERVICE_PROVIDERS: 'SERVICE_PROVIDERS'
};
export default function SearchBar({
  onTrackInteraction,
  sticky
}) {
  const mounted = useMounted();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState();
  const [optionsCache, setOptionsCache] = useState({});
  const [requestState, setRequestState] = useState(RequestStates.UNINITIALIZED);
  const [category, setCategory] = useState(SearchCategory.ALL);
  const [inputValue, setInputValue] = useState(null);
  const {
    fetcher: fetchThemeSearchBarResults
  } = useThemeSearchBarQuery({
    appContext: 'public',
    grouped: true,
    limit: 3
  });
  const {
    fetcher: fetchAppSearchBarResults
  } = useAppSearchBarQuery({
    appContext: 'public',
    grouped: true,
    limit: 3
  });
  const {
    fetcher: fetchProfileSearchBarResults
  } = useProfileSearchBarQuery({
    appContext: 'public',
    grouped: true,
    limit: 3
  });
  const fetchSearchBarResults = useCallback((_inputValue, _category) => {
    const cacheKey = `${_category}::${_inputValue}`;

    if (!_inputValue || _inputValue.trim().length < TYPEAHEAD_SEARCH_MIN_CHARS) {
      setOptions(undefined);
      return;
    } else if (cacheKey in optionsCache) {
      setOptions(optionsCache[cacheKey]);
      return;
    }

    setRequestState(RequestStates.PENDING);
    const promises = [];

    if (_category === SearchCategory.ALL || _category === SearchCategory.APPS) {
      promises.push(fetchAppSearchBarResults(_inputValue));
    }

    if (_category === SearchCategory.ALL || _category === SearchCategory.THEMES) {
      promises.push(fetchThemeSearchBarResults(_inputValue));
    }

    if (_category === SearchCategory.ALL || _category === SearchCategory.SERVICE_PROVIDERS) {
      promises.push(fetchProfileSearchBarResults(_inputValue));
    }

    Promise.all(promises).then(([...results]) => {
      const combinedResults = results.reduce((final, [optionGroup]) => [...final, optionGroup], []);

      if (mounted) {
        setOptions(combinedResults);
        setOptionsCache(Object.assign({}, optionsCache, {
          [cacheKey]: combinedResults
        }));
        setRequestState(RequestStates.SUCCEEDED);
      }

      return combinedResults;
    }).catch(error => {
      console.error(error);
    });
  }, [fetchAppSearchBarResults, fetchProfileSearchBarResults, fetchThemeSearchBarResults, mounted, optionsCache]);
  const categorySelectProps = useMemo(() => {
    return {
      onChange: ({
        target: {
          value
        }
      }) => {
        setCategory(value);
        onTrackInteraction(`select search category`, {
          category: value
        });
      },
      options: [{
        text: I18n.text('marketplaceUnified.searchBar.categorySelect.options.all'),
        value: SearchCategory.ALL
      }, {
        text: I18n.text('marketplaceUnified.searchBar.categorySelect.options.apps'),
        value: SearchCategory.APPS
      }, {
        text: I18n.text('marketplaceUnified.searchBar.categorySelect.options.themes'),
        value: SearchCategory.THEMES
      }, {
        text: I18n.text('marketplaceUnified.searchBar.categorySelect.options.serviceProviders'),
        value: SearchCategory.SERVICE_PROVIDERS
      }],
      value: category
    };
  }, [category, onTrackInteraction]);
  const handleInputValueChange = useCallback(({
    target: {
      value
    }
  }) => {
    if (value.trim().length >= TYPEAHEAD_SEARCH_MIN_CHARS) {
      setInputValue(value.trim());
    } else {
      setInputValue(null);
    }
  }, []);
  const debouncedHandleInputValueChange = useMemo(() => debounce(handleInputValueChange), [handleInputValueChange]);
  const handleClear = useCallback(() => {
    setInputValue(null);
    setOptions(undefined);
    setRequestState(RequestStates.UNINITIALIZED);
  }, []);
  const handleOpenChange = useCallback(openValue => {
    setOpen(openValue);
  }, []);
  const handleSearchSubmit = useCallback(value => {
    if (category !== SearchCategory.ALL) {
      let redirectUrl;

      switch (category) {
        case SearchCategory.APPS:
          {
            redirectUrl = `${getAppMarketplaceUrl()}/all-categories?eco_search=${value}`;
            break;
          }

        case SearchCategory.THEMES:
          {
            redirectUrl = `${getAssetMarketplaceUrl()}/all?eco_search=${value}`;
            break;
          }

        case SearchCategory.SERVICE_PROVIDERS:
          {
            redirectUrl = `${getSolutionsDirectoryUrl()}?eco_search=${value}`;
            break;
          }

        default:
          return;
      }

      onTrackInteraction('submit search', {
        category,
        search: value,
        searchLength: value.length
      });
      window.location.href = redirectUrl;
    }
  }, [category, onTrackInteraction]);
  useEffect(() => {
    if (open && inputValue) {
      fetchSearchBarResults(inputValue, category);
    }
  }, [category, fetchSearchBarResults, inputValue, open]);
  return /*#__PURE__*/_jsx(CoreSearchBar, {
    categorySelectProps: categorySelectProps,
    inputSize: sticky ? 'default' : 'xl',
    isLoading: isPending(requestState),
    onClear: handleClear,
    onInputValueChange: debouncedHandleInputValueChange,
    onOpenChange: handleOpenChange,
    onSearchSubmit: handleSearchSubmit,
    options: options,
    placeholder: unescapedText('marketplaceUnified.searchBar.placeholder'),
    use: "link"
  });
}